import React, { useState, useEffect } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  IconButton,
  Button,
  Grid,
  InputAdornment,
  TextField,
  Chip,
  Pagination,
  Switch,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Tooltip,
  Stack,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  Avatar,
  ListItemAvatar
} from '@mui/material';
import { FaSearch as SearchIcon, FaEye, FaEdit } from 'react-icons/fa';
import { TbCategoryPlus } from "react-icons/tb";
import { MdDeleteForever } from "react-icons/md";
import { getAllBogos, deleteBogo, updateBogo } from '../../Webservice/offerAPI';
import { useNavigate } from 'react-router-dom';
import { API_URL } from '../../Utils/url';

const getUserId = () => {
    let userId = null;
    ['user', 'adminUser', 'commonUser', 'sellerUser'].forEach(key => {
        if (userId) return;
        const u = sessionStorage.getItem(key) || localStorage.getItem(key);
        if (u) {
            try {
                const parsed = JSON.parse(u);
                userId = parsed._id || parsed.id || null;
            } catch (e) {}
        }
    });
    return userId || sessionStorage.getItem('userId') || localStorage.getItem('userId') || localStorage.getItem('commonUserId') || '';
};

const PAGE_SIZE = 20; // Default page size

function BogoOfferList() {
    const [Bogos, setBogos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [meta, setMeta] = useState({ page: 1, totalPages: 1, total: 0 });
    
    // Delete Dialog state
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [BogoToDelete, setBogoToDelete] = useState(null);

    // Products Dialog state
    const [productsDialogOpen, setProductsDialogOpen] = useState(false);
    const [selectedBogoProducts, setSelectedBogoProducts] = useState([]);

    const navigate = useNavigate();

    const fetchBogos = async (page = 1) => {
        setLoading(true);
        try {
            const params = {
                page,
                limit: PAGE_SIZE,
                ...(searchTerm && { search: searchTerm })
            };
            const response = await getAllBogos(params);
            
            // Assume the response follows our standard structure
            const BogoData = response.data || response.Bogos || [];
            setBogos(Array.isArray(BogoData) ? BogoData : []);
            
            const metaData = response.meta || { page: 1, totalPages: 1, total: BogoData.length };
            setMeta({
                page: metaData.page || 1,
                totalPages: metaData.totalPages || Math.ceil(BogoData.length / PAGE_SIZE) || 1,
                total: metaData.total || BogoData.length
            });
        } catch (error) {
            console.error('Fetch Bogos failed:', error);
            setBogos([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchBogos(meta.page);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [meta.page, searchTerm]);

    const handlePageChange = (event, newPage) => {
        setMeta((prev) => ({ ...prev, page: newPage }));
    };

    const handleToggleActive = async (Bogo) => {
        try {
            const newStatus = !Bogo.isActive;
            // Optimistic update
            setBogos(prev => prev.map(c => c._id === Bogo._id ? { ...c, isActive: newStatus } : c));
            const userId = getUserId();
            await updateBogo(Bogo._id, { 
                isActive: newStatus,
                action: 'Update',
                entity: 'Bogo Offer',
                createdBy: userId,
                activityLog: { action: 'Update', entity: 'Bogo Offer', createdBy: userId }
            });
        } catch (error) {
            console.error('Toggle active failed:', error);
            fetchBogos(meta.page);
        }
    };

    const handleDeleteClick = (Bogo) => {
        setBogoToDelete(Bogo);
        setDeleteDialogOpen(true);
    };

    const handleCloseDeleteDialog = () => {
        setDeleteDialogOpen(false);
        setBogoToDelete(null);
    };

    const handleConfirmDelete = async () => {
        if (BogoToDelete) {
            try {
                await deleteBogo(BogoToDelete._id);
                setDeleteDialogOpen(false);
                setBogoToDelete(null);
                fetchBogos(meta.page);
            } catch (error) {
                console.error('Delete Bogo failed:', error);
            }
        }
    };

    const handleProductsClick = (products) => {
        setSelectedBogoProducts(products || []);
        setProductsDialogOpen(true);
    };

    const handleCloseProductsDialog = () => {
        setProductsDialogOpen(false);
        setSelectedBogoProducts([]);
    };

    const calculateBogoStock = (products) => {
        if (!products || products.length === 0) return 0;
        let minStock = Infinity;
        products.forEach(item => {
            const p = item.product_id || item.category_id;
            if (!p) return;
            const available = p.availableQuantity || 0; // if it's category, available is undefined, we might just return default
            const possibleBogos = Math.floor(available / 1); // For Bogos, qty is checked against total stock
            if (possibleBogos < minStock) minStock = possibleBogos;
        });
        return minStock === Infinity ? 0 : minStock;
    };

    return (
        <Box p={2} style={{ background: '#f8fafc', borderRadius: '10px' }}>
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                <Typography variant="h3" component="h1" sx={{
                    color: 'text.primary',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    fontFamily: 'Mulish, sans-serif',
                }}>
                    Bogo Offers Management
                    <Chip
                        label={`${meta.total} ${meta.total === 1 ? 'Bogo' : 'Bogos'}`}
                        size="small"
                        sx={{
                            backgroundColor: 'rgba(255, 209, 2, 0.2)',
                            color: '#b39200',
                            fontFamily: 'Mulish, sans-serif',
                            fontWeight: '800',
                            border: '1px solid rgba(255, 209, 2, 0.5)'
                        }}
                    />
                </Typography>

                <Button
                    variant="contained"
                    startIcon={<TbCategoryPlus />}
                    sx={{
                        fontFamily: 'Mulish, sans-serif',
                        background: 'rgb(255 209 2)',
                        color: 'black',
                        textTransform: 'none',
                        borderRadius: '10px',
                        fontWeight: 700,
                        fontSize: '0.9rem',
                        px: 3,
                        boxShadow: '0 4px 12px rgba(255, 209, 2, 0.3)',
                        border: '1px solid black',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                            background: 'rgb(255 209 2)',
                            boxShadow: '0 6px 16px rgba(255, 209, 2, 0.4)',
                            transform: 'translateY(-1px)'
                        }
                    }}
                    onClick={() => navigate('/offers/bogo/create')}
                >
                    Add Bogo
                </Button>
            </Box>

            {/* Searchbar */}
            <Box sx={{
                mb: 3,
                p: 2,
                backgroundColor: '#f8fafc',
                borderRadius: 2,
            }}>
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} md={4}>
                        <TextField
                            fullWidth
                            variant="outlined"
                            placeholder="Search Bogos..."
                            size="small"
                            value={searchTerm}
                            onChange={(e) => {
                                setSearchTerm(e.target.value);
                                setMeta({ ...meta, page: 1 });
                            }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon color="action" />
                                    </InputAdornment>
                                ),
                                sx: { borderRadius: 2 }
                            }}
                        />
                    </Grid>
                </Grid>
            </Box>

            <TableContainer component={Paper} elevation={0} sx={{ borderRadius: '8px', boxShadow: '0 4px 20px rgba(0,0,0,0.08)', border: '1px solid #d3d3d3de' }}>
                <Table stickyHeader>
                    <TableHead>
                        <TableRow sx={{
                            '& th': {
                                backgroundColor: '#f8f9fa',
                                fontWeight: 800,
                                py: 2,
                                fontSize: '0.75rem',
                                textTransform: 'uppercase',
                                letterSpacing: '0.05em',
                                color: '#5c636a',
                                fontFamily: 'Mulish, sans-serif',
                                borderBottom: '2px solid #edeff1',
                            }
                        }}>
                            <TableCell>Bogo Details</TableCell>
                            <TableCell align="center">Products</TableCell>
                            <TableCell align="center">Stock</TableCell>
                            <TableCell align="center">Buy (Qty)</TableCell>
                            <TableCell align="center">Get (Qty)</TableCell>
                            <TableCell align="center">Live Status</TableCell>
                            <TableCell align="right">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading ? (
                            <TableRow>
                                <TableCell colSpan={8} align="center" sx={{ py: 3 }}>
                                    <CircularProgress />
                                </TableCell>
                            </TableRow>
                        ) : Bogos.length > 0 ? (
                            Bogos.map((Bogo) => (
                                <TableRow
                                    key={Bogo._id}
                                    hover
                                    sx={{ '&:hover': { backgroundColor: '#f9fafb' }, transition: 'background-color 0.2s ease' }}
                                >
                                    <TableCell sx={{ cursor: 'pointer' }} onClick={() => navigate(`/offers/bogo/view/${Bogo._id}`)}>
                                        <Typography fontWeight={700} sx={{ fontFamily: 'Mulish, sans-serif', fontSize: '0.95rem', color: '#1a1c1e', '&:hover': { color: '#0d47a1' } }}>
                                            {Bogo.title}
                                        </Typography>
                                        <Typography variant="caption" sx={{ fontFamily: 'Mulish, sans-serif', color: '#6b7280', display: 'inline-block', backgroundColor: '#f3f4f6', px: 1, borderRadius: '4px', mt: 1, fontWeight: 600 }}>
                                            Code: {Bogo.BogoCode || 'N/A'}
                                        </Typography>
                                    </TableCell>
                                    <TableCell align="center">
                                        <Chip 
                                            label={`${Bogo.buyProducts?.length || 0} Products`} 
                                            size="small" 
                                            onClick={() => handleProductsClick(Bogo.buyProducts)}
                                            sx={{ 
                                                fontWeight: 800, 
                                                cursor: 'pointer',
                                                backgroundColor: 'rgba(33, 150, 243, 0.1)',
                                                color: '#1976d2',
                                                '&:hover': { backgroundColor: 'rgba(33, 150, 243, 0.2)' }
                                            }} 
                                        />
                                    </TableCell>
                                    <TableCell align="center">
                                        {(() => {
                                            const stock = calculateBogoStock(Bogo.buyProducts);
                                            return (
                                                <Chip 
                                                    label={stock > 0 ? `In Stock (${stock})` : 'Out of Stock'} 
                                                    size="small" 
                                                    sx={{ 
                                                        fontWeight: 800, 
                                                        color: stock > 0 ? '#059669' : '#dc2626', 
                                                        backgroundColor: stock > 0 ? '#d1fae5' : '#fee2e2' 
                                                    }} 
                                                />
                                            );
                                        })()}
                                    </TableCell>
                                    <TableCell align="center">
                                        <Typography fontWeight={700} sx={{ fontFamily: 'Mulish, sans-serif', color: '#1a1c1e', fontSize: '0.95rem' }}>
                                            {Bogo.buyQuantity || 1}
                                        </Typography>
                                    </TableCell>
                                    <TableCell align="center">
                                        <Typography fontWeight={700} sx={{ fontFamily: 'Mulish, sans-serif', color: '#059669', fontSize: '0.95rem' }}>
                                            {Bogo.getQuantity || 1}
                                        </Typography>
                                    </TableCell>
                                    <TableCell align="center">
                                        <Switch
                                            size="small"
                                            checked={!!Bogo.isActive}
                                            onChange={() => handleToggleActive(Bogo)}
                                            color="primary"
                                        />
                                    </TableCell>
                                    <TableCell align="right">
                                        <Stack direction="row" spacing={1} justifyContent="flex-end">
                                            <Tooltip title="View Bogo">
                                                <IconButton
                                                    size="small"
                                                    sx={{ color: '#2196f3', backgroundColor: 'rgba(33, 150, 243, 0.1)', '&:hover': { backgroundColor: 'rgba(33, 150, 243, 0.2)' }, borderRadius: '8px' }}
                                                    onClick={() => navigate(`/offers/bogo/view/${Bogo._id}`)}
                                                >
                                                    <FaEye />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Edit Bogo">
                                                <IconButton
                                                    size="small"
                                                    sx={{ color: '#4682B4', backgroundColor: 'rgba(70, 130, 180, 0.1)', '&:hover': { backgroundColor: 'rgba(70, 130, 180, 0.2)' }, borderRadius: '8px' }}
                                                    onClick={() => navigate(`/offers/bogo/edit/${Bogo._id}`)}
                                                >
                                                    <FaEdit />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Delete">
                                                <IconButton
                                                    size="small"
                                                    sx={{ color: '#dc2626', backgroundColor: 'rgba(220, 38, 38, 0.1)', '&:hover': { backgroundColor: 'rgba(220, 38, 38, 0.2)' }, borderRadius: '8px' }}
                                                    onClick={() => handleDeleteClick(Bogo)}
                                                >
                                                    <MdDeleteForever size={20} />
                                                </IconButton>
                                            </Tooltip>
                                        </Stack>
                                    </TableCell>
                                </TableRow>
                            ))
                        ) : (
                            <TableRow>
                                <TableCell colSpan={8} sx={{ textAlign: 'center', py: 6, color: 'text.secondary' }}>
                                    <Typography variant="body1">No Bogos available</Typography>
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3, '& .MuiPagination-ul': { flexWrap: 'nowrap' } }}>
                <Pagination
                    count={meta.totalPages}
                    page={meta.page}
                    onChange={handlePageChange}
                    sx={{
                        '& .MuiPaginationItem-root': {
                            fontFamily: 'Mulish, sans-serif',
                            fontWeight: 600,
                            borderRadius: '8px',
                            '&.Mui-selected': { backgroundColor: 'rgb(255, 209, 2)', color: 'black', '&:hover': { backgroundColor: 'rgb(255, 190, 0)' } }
                        }
                    }}
                    shape="rounded"
                    showFirstButton
                    showLastButton
                />
            </Box>

            <Dialog
                open={deleteDialogOpen}
                onClose={handleCloseDeleteDialog}
                PaperProps={{
                    sx: { borderRadius: '12px', padding: '8px', minWidth: '400px' }
                }}
            >
                <DialogTitle sx={{ fontFamily: 'Mulish, sans-serif', fontWeight: 800, color: '#1a1c1e', pb: 1 }}>
                    Delete Bogo
                </DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ fontFamily: 'Mulish, sans-serif', color: '#4b5563', fontSize: '0.95rem' }}>
                        Are you sure you want to delete the Bogo <strong>{BogoToDelete?.title}</strong>? This action cannot be undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ padding: '16px' }}>
                    <Button onClick={handleCloseDeleteDialog} variant="outlined" sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 600, color: '#4b5563', borderColor: '#d1d5db' }}>
                        Cancel
                    </Button>
                    <Button onClick={handleConfirmDelete} variant="contained" sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 600, backgroundColor: '#dc2626', color: 'white', '&:hover': { backgroundColor: '#b91c1c' } }}>
                        Delete Bogo
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Products List Dialog */}
            <Dialog
                open={productsDialogOpen}
                onClose={handleCloseProductsDialog}
                PaperProps={{
                    sx: { borderRadius: '12px', padding: '8px', minWidth: '450px' }
                }}
            >
                <DialogTitle sx={{ fontFamily: 'Mulish, sans-serif', fontWeight: 800, color: '#1a1c1e', pb: 1, borderBottom: '1px solid #e5e7eb' }}>
                    Included Products/Categories
                </DialogTitle>
                <DialogContent sx={{ p: 0 }}>
                    <List sx={{ width: '100%', bgcolor: 'background.paper', p: 0 }}>
                        {selectedBogoProducts.length > 0 ? (
                            selectedBogoProducts.map((item, index) => {
                                const isProduct = !!item.product_id;
                                const target = item.product_id || item.category_id;
                                if (!target) return null;
                                
                                const imageUrl = target.product_images?.[0] 
                                    ? (target.product_images[0].startsWith('http') ? target.product_images[0] : `${API_URL.replace(/\/api\/?$/, '/')}${target.product_images[0]}`) 
                                    : '/img/default-product.png';

                                return (
                                    <ListItem key={index} sx={{ borderBottom: '1px solid #f3f4f6' }}>
                                        {isProduct && (
                                            <ListItemAvatar>
                                                <Avatar variant="rounded" src={imageUrl} />
                                            </ListItemAvatar>
                                        )}
                                        <ListItemText 
                                            primary={
                                                <Typography fontWeight={700} sx={{ fontFamily: 'Mulish, sans-serif' }}>
                                                    {target.title || target.name || 'Unknown Item'}
                                                </Typography>
                                            } 
                                            secondary={`Type: ${isProduct ? 'Product' : 'Category'}`} 
                                        />
                                    </ListItem>
                                );
                            })
                        ) : (
                            <Box p={3} textAlign="center">
                                <Typography color="text.secondary">No items found.</Typography>
                            </Box>
                        )}
                    </List>
                </DialogContent>
                <DialogActions sx={{ padding: '16px', borderTop: '1px solid #e5e7eb' }}>
                    <Button onClick={handleCloseProductsDialog} variant="contained" sx={{ background: '#1976d2', color: 'white', borderRadius: '8px', textTransform: 'none', fontWeight: 600 }}>
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

export default BogoOfferList;
